import { useState } from "react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { MessageCircle, Mail, Send, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const DEFAULT_SERVICES = [
  "Business Website",
  "Landing Page",
  "Portfolio Website",
  "E-commerce Website",
  "Web Application",
  "Website Redesign",
  "Website Maintenance",
  "Other / Not sure yet",
];

const DEFAULT_BUDGETS = ["Under ₹5,000", "₹5,000 – ₹15,000", "₹15,000 – ₹30,000", "₹30,000+", "Not sure yet"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors duration-200 focus:border-[#00F0FF]/60 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/30";

const initialForm = { name: "", business_name: "", phone: "", email: "", service: "", budget: "", message: "" };

export default function Contact({ contact, formOptions }) {
  const SERVICES = formOptions?.services?.length ? formOptions.services : DEFAULT_SERVICES;
  const BUDGETS = formOptions?.budgets?.length ? formOptions.budgets : DEFAULT_BUDGETS;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (form.phone.trim().length < 5) errs.phone = "Please enter a phone or WhatsApp number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email.";
    if (!form.service) errs.service = "Please choose a service.";
    if (form.message.trim().length < 10) errs.message = "Tell us a little about your project (min 10 characters).";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent! We'll get back to you shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const waLink = `https://wa.me/${contact.whatsapp_number}?text=${encodeURIComponent(
    "Hi Tarbyte! I'd like to discuss a website project."
  )}`;

  return (
    <section id="contact" className="py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2">
        <div>
          <Reveal>
            <Eyebrow>Contact Us</Eyebrow>
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Let's build something that grows your business.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
              Tell us about your project and we'll reply with next steps and a free consultation —
              usually within one business day.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 space-y-4">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0A0F1C] p-5 transition-colors duration-300 hover:border-[#00F0FF]/50"
              >
                <span className="rounded-xl bg-[#00F0FF]/10 p-3 text-[#00F0FF]">
                  <MessageCircle size={22} />
                </span>
                <span>
                  <span className="block font-display text-sm font-bold text-white">WhatsApp us</span>
                  <span className="block text-sm text-slate-400" data-testid="contact-whatsapp-number">
                    {contact.whatsapp_display}
                  </span>
                </span>
              </a>
              <a
                href={`mailto:${contact.email}`}
                data-testid="contact-email-btn"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#0A0F1C] p-5 transition-colors duration-300 hover:border-[#00F0FF]/50"
              >
                <span className="rounded-xl bg-[#00F0FF]/10 p-3 text-[#00F0FF]">
                  <Mail size={22} />
                </span>
                <span>
                  <span className="block font-display text-sm font-bold text-white">Email us</span>
                  <span className="block text-sm text-slate-400" data-testid="contact-email-address">
                    {contact.email}
                  </span>
                </span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            noValidate
            data-testid="contact-form"
            className="rounded-3xl border border-white/10 bg-[#0A0F1C] p-8"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">Name *</label>
                <input id="cf-name" data-testid="contact-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
                {errors.name && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-name-error">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="cf-business" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">Business name</label>
                <input id="cf-business" data-testid="contact-business-input" className={inputCls} placeholder="Your business" value={form.business_name} onChange={set("business_name")} />
              </div>
              <div>
                <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">Phone / WhatsApp *</label>
                <input id="cf-phone" data-testid="contact-phone-input" className={inputCls} placeholder="+1 555 000 0000" value={form.phone} onChange={set("phone")} />
                {errors.phone && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-phone-error">{errors.phone}</p>}
              </div>
              <div>
                <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">Email *</label>
                <input id="cf-email" type="email" data-testid="contact-email-input" className={inputCls} placeholder="you@company.com" value={form.email} onChange={set("email")} />
                {errors.email && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-email-error">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="cf-service" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">Service needed *</label>
                <select id="cf-service" data-testid="contact-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                  <option value="" className="bg-[#0A0F1C]">Select a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s} className="bg-[#0A0F1C]">{s}</option>
                  ))}
                </select>
                {errors.service && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-service-error">{errors.service}</p>}
              </div>
              <div>
                <label htmlFor="cf-budget" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">Budget range</label>
                <select id="cf-budget" data-testid="contact-budget-select" className={inputCls} value={form.budget} onChange={set("budget")}>
                  <option value="" className="bg-[#0A0F1C]">Select a range…</option>
                  {BUDGETS.map((b) => (
                    <option key={b} value={b} className="bg-[#0A0F1C]">{b}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">Project details *</label>
                <textarea id="cf-message" rows={4} data-testid="contact-message-input" className={inputCls} placeholder="Tell us about your business and what you need…" value={form.message} onChange={set("message")} />
                {errors.message && <p className="mt-1.5 text-xs text-red-400" data-testid="contact-message-error">{errors.message}</p>}
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-btn"
              className="mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-[#00F0FF] px-6 py-4 text-sm font-bold text-[#050811] transition-shadow duration-300 hover:shadow-[0_0_32px_rgba(0,240,255,0.55)] disabled:opacity-60"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              We only use your details to respond to this enquiry. No spam, ever.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
